/**
 * useKaysouAssist — estado do tablet de sala (Atendimento de Mesas).
 *
 * Tempo real por canal próprio de atendimento (/api/table-assist/ws/...),
 * separado do KDS. Polling curto como fallback seguro. Se o tablet
 * reiniciar ou perder ligação, os chamados ativos voltam do banco pelo
 * snapshot inicial do WebSocket e pelo GET de segurança.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API_URL } from "@/utils/apiConfig";
import { countNew, mergeRequest } from "@/utils/kaysouAssistState";

const POLL_MS = 4000;
const SAFETY_POLL_MS = 20000;
const SOUND_DEBOUNCE_MS = 1500;

function readRestaurantId() {
  try {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    return t ? JSON.parse(atob(t.split(".")[1])).restaurant_id || null : null;
  } catch {
    return null;
  }
}

export function useKaysouAssist() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [actionError, setActionError] = useState(null);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const soundOnRef = useRef(false);
  const lastSoundRef = useRef(0);
  const knownNewRef = useRef(0);

  const playAlert = useCallback(() => {
    // Um único som por vaga de chamados novos — quatro mesas não tocam quatro vezes.
    if (!soundOnRef.current) return;
    const now = Date.now();
    if (now - lastSoundRef.current < SOUND_DEBOUNCE_MS) return;
    lastSoundRef.current = now;
    try {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state !== "running") return;
      // Sino curto de atendimento: duas badaladas com harmónicos e queda natural.
      [[784, 0, 0.55, 0.42], [1568, 0, 0.38, 0.16],
       [1046, 0.32, 0.95, 0.42], [2092, 0.32, 0.72, 0.14]]
        .forEach(([freq, start, end, volume]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
          gain.gain.setValueAtTime(volume, ctx.currentTime + start);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + end);
          osc.start(ctx.currentTime + start);
          osc.stop(ctx.currentTime + end);
        });
    } catch { /* som é acessório: nunca pode partir o quadro */ }
  }, []);

  const enableSound = useCallback(async () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === "suspended") await audioCtxRef.current.resume();
      soundOnRef.current = true;
      setSoundOn(true);
    } catch {
      setSoundOn(false);
    }
  }, []);

  const applyList = useCallback((list) => {
    const incoming = Array.isArray(list) ? list : [];
    const newCount = countNew(incoming);
    if (newCount > knownNewRef.current) playAlert();
    knownNewRef.current = newCount;
    setRequests(incoming);
  }, [playAlert]);

  const refresh = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/table-assist/requests");
      applyList(res.data?.requests || []);
    } catch {
      // Falha transitória: o quadro mantém o que já mostra.
    }
    setLoading(false);
  }, [applyList]);

  const act = useCallback(async (requestId, action) => {
    setActionError(null);
    try {
      const res = await authAxiosRef.current.post(`/table-assist/requests/${requestId}/${action}`);
      const updated = res.data?.request;
      if (updated) setRequests((prev) => mergeRequest(prev, updated));
    } catch (err) {
      setActionError(err?.response?.data?.detail || "No se pudo actualizar la llamada.");
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    let closed = false;
    const restaurantId = readRestaurantId();
    refresh();
    if (!restaurantId) return undefined;

    let reconnectTimer = null;
    let attempt = 0;
    let heartbeat = null;
    let pongTimeout = null;
    const stopHeartbeat = () => {
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
      if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
    };

    const connect = () => {
      if (closed) return;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      if (!token) return;
      const url = `${(API_URL || "").replace(/^http/, "ws")}/api/table-assist/ws/${restaurantId}?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          setWsConnected(true);
          attempt = 0;
          stopHeartbeat();
          heartbeat = setInterval(() => {
            if (ws.readyState !== WebSocket.OPEN) return;
            try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* fechado */ }
            pongTimeout = setTimeout(() => { try { ws.close(); } catch { /* já fechado */ } }, 5000);
          }, 25000);
        };
        ws.onmessage = (event) => {
          if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
          try {
            const data = JSON.parse(event.data);
            if (data.event === "pong") return;
            if (data.event === "assist_snapshot") {
              applyList(data.requests || []);
            } else if (data.event === "assist_request_created" && data.request) {
              setRequests((prev) => mergeRequest(prev, data.request));
              knownNewRef.current += 1;
              playAlert();
            } else if (data.event === "assist_request_updated" && data.request) {
              setRequests((prev) => mergeRequest(prev, data.request));
            }
          } catch { /* mensagem inválida é ignorada */ }
        };
        ws.onclose = () => {
          stopHeartbeat();
          setWsConnected(false);
          if (closed) return;
          attempt += 1;
          reconnectTimer = setTimeout(connect, Math.min(1000 * 2 ** (attempt - 1), 30000));
        };
        ws.onerror = () => { try { ws.close(); } catch { /* já fechado */ } };
      } catch {
        setWsConnected(false);
      }
    };
    connect();

    const poll = setInterval(() => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) refresh();
    }, POLL_MS);
    const safety = setInterval(refresh, SAFETY_POLL_MS);

    return () => {
      closed = true;
      stopHeartbeat();
      clearInterval(poll);
      clearInterval(safety);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* já fechado */ }
    };
  }, [refresh, applyList, playAlert]);

  return {
    requests, loading, wsConnected, soundOn, actionError,
    enableSound, refresh,
    acceptRequest: (id) => act(id, "accept"),
    completeRequest: (id) => act(id, "complete"),
  };
}
